import React, { useEffect } from 'react'
import { Map } from 'immutable'
import { connect as connectRedux } from 'react-redux'
import compose from 'recompose/compose'
import Button, { TYPES } from '../../Button/index'
import { formatPrice } from '../../../services/plans/index'
import { connect as connectStaticText } from '../../StaticText/connect'
import { getBoundActions } from '../../../actions/index'
import { PAYMENT_INFO_PAGE_GA4_EVENT } from '../../../services/event-tracking/index'
import { isTaxable } from '../../../services/user-account/index'
import CartSinglePagePaymentInfo from '../CartSinglePagePayment/CartSinglePagePaymentInfo'
import { handleRestartSubscription, hasPaymentData, WINBACK_STEPS } from '../utils'
/**
 * Component to handle payment setup and subscription restart
 * @param {Map} props.staticText - Static text translations
 * @param {Map} props.userAccount - User account data
 * @param {Map} props.velocityCheck - Velocity check data
 * @param {Map} props.checkout - Checkout data
 * @param {Map} props.selectedPlan - Selected plan data
 * @param {String} props.userLanguage - User language
 * @param {Function} props.initAdyen - Function to initialize Adyen
 * @param {Function} props.setPlansSelection - Function to set plan selection
 * @param {Function} props.setRenderProcessing - Function to set render processing
 * @param {Function} props.setCheckoutWinbackStep - Function to set checkout winback step
 * @param {Function} props.setCheckoutWinbackUseDefaultPayment
 */
function CartSinglePagePaymentSection (props) {
  const {
    staticText,
    userAccount,
    velocityCheck,
    checkout,
    selectedPlan = Map(),
    userLanguage,
    initAdyen,
    setPlansSelection,
    setRenderWinback,
    setEventDefault,
    setRenderProcessing,
    setCheckoutWinbackStep,
    setCheckoutSinglePageStep,
    sendOptimizelyFeatureEvent,
    setCheckoutWinbackUseDefaultPayment,
  } = props
  const cardExpYear = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'paymentSource', 'expYear'])
  const isCardExpired = cardExpYear ? parseInt(cardExpYear, 10) < new Date().getFullYear() : false
  const velocityCheckToken = velocityCheck.getIn(['data', 'velocityCheck', 'token'])
  const orderError = checkout.get('orderError')
  const userHasPaymentData = hasPaymentData(props)

  // Check for order errors
  useEffect(() => {
    if (orderError) {
      setCheckoutWinbackUseDefaultPayment(false)
      setCheckoutWinbackStep(WINBACK_STEPS.SELECT_PAYMENT)
      setRenderProcessing(false)
      setRenderWinback(false)
    }
  }, [orderError])

  // Initialize Adyen if no velocity check token
  useEffect(() => {
    if (!velocityCheckToken) {
      initAdyen()
    }
  }, [orderError, initAdyen])

  // Remove loading when the user selected plan is set
  useEffect(() => {
    if (selectedPlan) {
      setRenderProcessing(false)
    }
  }, [selectedPlan])

  const handleChangePayment = () => {
    // Update the plan selection
    setPlansSelection(selectedPlan)

    // GA4 + optimizely event for Payment Set Up screen
    setEventDefault(PAYMENT_INFO_PAGE_GA4_EVENT)
    sendOptimizelyFeatureEvent({ eventName: 'payment_info_page' })

    // Set Checkout step
    setCheckoutSinglePageStep(3, 6)

    // Update the winback step
    setRenderWinback(false)
    setCheckoutWinbackStep(WINBACK_STEPS.SELECT_PAYMENT)
    setCheckoutWinbackUseDefaultPayment(false)
  }

  // Early return if no selected plan
  if (!selectedPlan) {
    return null
  }

  const onClickSubscription = () => {
    setRenderProcessing(true)
    setPlansSelection(selectedPlan)
    handleRestartSubscription({ ...props, selectedPlan })
  }

  // Show payment setup button if no billing data or card expired
  if (!userHasPaymentData || isCardExpired) {
    return (
      <div className="winback__payment">
        <Button
          type={TYPES.PRIMARY}
          onClick={() => handleChangePayment()}
          className="winback__payment-setup-button"
        >
          {staticText.getIn(['data', 'setUpPayment'])}
        </Button>
      </div>
    )
  }

  // Calculate price for display
  const cost = selectedPlan.getIn(['costs', 0]) || 0
  const currencyIso = selectedPlan.get('currencyIso')
  const price = formatPrice(currencyIso, cost, false, null, userLanguage)
  const preTaxSuffix = isTaxable(props)
    ? ` (${staticText.getIn(['data', 'preTax'])})`
    : ''

  return (
    <div className="winback__payment">
      <CartSinglePagePaymentInfo onChangePaymentClick={() => handleChangePayment()} />
      <Button
        type={TYPES.PRIMARY}
        onClick={onClickSubscription}
        className="winback__button winback__payment-restart-button"
      >
        {`${staticText.getIn(['data', 'restartSubscription'])} - ${price}`}
        { preTaxSuffix && <span className="winback__payment-restart-button-pretax">{preTaxSuffix}</span> }
      </Button>
    </div>
  )
}

export default compose(
  connectStaticText({ storeKey: 'CartSinglePage' }),
  connectRedux(
    (state) => ({
      auth: state.auth,
      user: state.user,
      userAccount: state.userAccount,
      velocityCheck: state.velocityCheck,
      inboundTracking: state.inboundTracking,
      userLanguage: state.userLanguage,
      velocityCheckToken: state.velocityCheck.getIn(['data', 'velocityCheck', 'token']),
      paytrack: state.paytrack,
      plans: state.plans,
      checkout: state.checkout,
    }),
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        initAdyen: actions.adyen.initAdyen,
        processCheckoutOrder: actions.checkout.processCheckoutOrder,
        setPlansSelection: actions.plans.setPlansSelection,
        setCheckoutWinbackUseDefaultPayment: actions.checkout.setCheckoutWinbackUseDefaultPayment,
        setCheckoutWinbackStep: actions.checkout.setCheckoutWinbackStep,
        setEventDefault: actions.eventTracking.setEventDefault,
        setCheckoutSinglePageStep: actions.checkout.setCheckoutSinglePageStep,
        sendOptimizelyFeatureEvent: actions.optimizelyFeature.sendOptimizelyFeatureEvent,
      }
    },
  ),
)(CartSinglePagePaymentSection)
